import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { acquisitionsApi, type AcquisitionRequest, type Fund } from "@/lib/api/acquisitions";
import { downloadCsv } from "@/lib/csv-export";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { FileText, Plus, Download, CheckSquare, Loader2 } from "lucide-react";

const statusVariantMap: Record<
  string,
  { variant: "success" | "warning" | "info" | "danger" | "neutral" | "purple"; label: string }
> = {
  DRAFT: { variant: "neutral", label: "Draft" },
  SUBMITTED: { variant: "info", label: "Submitted" },
  UNDER_REVIEW: { variant: "purple", label: "Under Review" },
  NEEDS_INFO: { variant: "warning", label: "Needs Info" },
  APPROVED: { variant: "success", label: "Approved" },
  REJECTED: { variant: "danger", label: "Rejected" },
  ORDERED: { variant: "info", label: "Ordered" },
  PARTIALLY_RECEIVED: { variant: "warning", label: "Partially Received" },
  FULLY_RECEIVED: { variant: "success", label: "Fully Received" },
  INVOICE_MATCHED: { variant: "purple", label: "Invoice Matched" },
  CATALOGED: { variant: "success", label: "Cataloged" },
  CLOSED: { variant: "neutral", label: "Closed" },
};

const urgencyVariant: Record<string, "danger" | "warning" | "neutral"> = {
  HIGH: "danger",
  MEDIUM: "warning",
  LOW: "neutral",
};

export default function AcquisitionRequestsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAccessControl();
  const toast = useToast();
  const canBulkApprove = hasPermission("acquisitions.approveRequest");
  const [requests, setRequests] = useState<AcquisitionRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState<string>("all");
  const [fundFilter, setFundFilter] = useState<string>("all");
  const [fundOptions, setFundOptions] = useState<Fund[]>([]);

  useEffect(() => {
    acquisitionsApi.funds
      .list({ isActive: true, limit: 200 })
      .then((r) => setFundOptions(r.funds))
      .catch(() => setFundOptions([]));
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await acquisitionsApi.requests.list({
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        source: sourceFilter !== "all" ? sourceFilter : undefined,
        urgency: urgencyFilter !== "all" ? urgencyFilter : undefined,
        fundId: fundFilter !== "all" ? fundFilter : undefined,
        createdFrom: createdFrom.trim() || undefined,
        createdTo: createdTo.trim() || undefined,
        limit: 50,
      });
      setRequests(res.requests);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load requests", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sourceFilter, urgencyFilter, fundFilter, createdFrom, createdTo]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const approveableIds = requests
    .filter((r) => r.status === "SUBMITTED" || r.status === "UNDER_REVIEW")
    .map((r) => r.id);

  const handleBulkApprove = async () => {
    const ids = approveableIds.filter((id) => selectedIds.has(id));
    if (ids.length === 0) {
      toast.error("Select at least one submitted or under-review request.");
      return;
    }
    setBulkApproving(true);
    try {
      const res = await acquisitionsApi.requests.bulkApprove(ids);
      if (res.approved.length) {
        toast.success(`Approved ${res.approved.length} request(s).`);
      }
      if (res.failed.length) {
        toast.error(
          `${res.failed.length} failed: ${res.failed
            .slice(0, 3)
            .map((f) => f.reason)
            .join("; ")}`
        );
      }
      setSelectedIds(new Set());
      await fetchRequests();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Bulk approve failed."));
    } finally {
      setBulkApproving(false);
    }
  };

  const columns: DataTableColumn<AcquisitionRequest>[] = [
    {
      id: "title",
      header: "Title",
      cell: (row) => (
        <button
          onClick={() => navigate(`/acquisitions/requests/${row.id}`)}
          className="text-left font-medium text-primary hover:underline"
        >
          <div className="max-w-xs truncate">{row.title}</div>
          {row.author && <div className="text-xs text-slate-500 truncate">{row.author}</div>}
        </button>
      ),
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => (
        <StatusBadge variant={row.source === "PATRON" ? "info" : "neutral"}>
          {row.source === "PATRON" ? "Patron" : "Staff"}
        </StatusBadge>
      ),
      width: "w-20",
      hideOnMobile: true,
    },
    {
      id: "urgency",
      header: "Urgency",
      cell: (row) => (
        <StatusBadge variant={urgencyVariant[row.urgency] || "neutral"}>{row.urgency}</StatusBadge>
      ),
      width: "w-24",
      hideOnMobile: true,
    },
    {
      id: "quantity",
      header: "Qty",
      cell: (row) => <span className="text-sm tabular-nums">{row.quantity}</span>,
      width: "w-12",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "fund",
      header: "Fund",
      cell: (row) => <span className="text-sm text-slate-600">{row.fund?.name || "—"}</span>,
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => {
        const cfg = statusVariantMap[row.status] || {
          variant: "neutral" as const,
          label: row.status,
        };
        return <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>;
      },
      width: "w-32",
    },
    {
      id: "date",
      header: "Created",
      cell: (row) => (
        <span className="text-xs text-slate-500">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      ),
      width: "w-24",
      hideOnMobile: true,
    },
  ];

  const exportCsv = () => {
    const headers = [
      "ID",
      "Title",
      "Author",
      "ISBN",
      "Source",
      "Urgency",
      "Qty",
      "Fund",
      "Status",
      "Created",
    ];
    const rows = requests.map((r) => [
      r.id,
      r.title,
      r.author ?? "",
      r.isbn ?? "",
      r.source,
      r.urgency,
      String(r.quantity),
      r.fund?.name ?? "",
      statusVariantMap[r.status]?.label ?? r.status,
      new Date(r.createdAt).toISOString(),
    ]);
    downloadCsv(`acquisition-requests-${new Date().toISOString().slice(0, 10)}`, headers, rows);
  };

  const statuses = [
    "DRAFT",
    "SUBMITTED",
    "UNDER_REVIEW",
    "NEEDS_INFO",
    "APPROVED",
    "REJECTED",
    "ORDERED",
    "PARTIALLY_RECEIVED",
    "FULLY_RECEIVED",
    "INVOICE_MATCHED",
    "CATALOGED",
    "CLOSED",
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Purchase Requests"
        description="Manage acquisition and recommendation requests"
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Purchase Requests" },
        ]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title, author, ISBN..."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canBulkApprove && (
              <Button
                type="button"
                variant="secondary"
                disabled={bulkApproving || loading || approveableIds.length === 0}
                onClick={() => handleBulkApprove()}
              >
                {bulkApproving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckSquare className="mr-2 h-4 w-4" />
                )}
                Approve selected
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={loading || requests.length === 0}
              onClick={exportCsv}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button onClick={() => navigate("/acquisitions/requests/new")}>
              <Plus className="mr-2 h-4 w-4" />
              New Request
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s} value={s}>
                {statusVariantMap[s]?.label || s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="PATRON">Patron</SelectItem>
            <SelectItem value="STAFF">Staff</SelectItem>
          </SelectContent>
        </Select>
        <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Urgency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All urgency</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fundFilter} onValueChange={setFundFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Fund" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All funds</SelectItem>
            {fundOptions.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name} ({f.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Created from</Label>
            <Input
              type="date"
              className="w-[140px] h-9"
              value={createdFrom}
              onChange={(e) => setCreatedFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Created to</Label>
            <Input
              type="date"
              className="w-[140px] h-9"
              value={createdTo}
              onChange={(e) => setCreatedTo(e.target.value)}
            />
          </div>
        </div>
        <span className="text-sm text-slate-500">
          {total} request{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={requests}
        rowKey={(row) => row.id}
        loading={loading}
        selectable={canBulkApprove}
        selectedIds={selectedIds}
        onSelectChange={setSelectedIds}
        rowSelectable={(row) => row.status === "SUBMITTED" || row.status === "UNDER_REVIEW"}
        emptyState={
          <EmptyState
            icon={<FileText className="h-6 w-6 text-slate-400" />}
            title="No requests yet"
            description="Create a purchase request or wait for patron recommendations."
            action={
              <Button onClick={() => navigate("/acquisitions/requests/new")}>
                <Plus className="mr-2 h-4 w-4" />
                New Request
              </Button>
            }
          />
        }
      />
    </PageContainer>
  );
}

export { statusVariantMap };
